import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Copy, ExternalLink, Eye, EyeOff, ImagePlus, Instagram, Loader2, Lock, Package, Plus, Save, Store,
    Trash2, Undo2, Upload, Wand2,
} from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import ConsignmentSectionBar from "@/components/account/ConsignmentSectionBar";
import { Btn, PageHead, Pill, Section, Toggle, TONES, cellCls, inputCls } from "@/components/account/Shell";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { READY_IN } from "@/lib/nav";

/** The three ways a seller can carry one of 13thav Demo's products. */
const MODES = [
    ["consignment", "Consignment", "13thav Demo prints and ships it under your name. Locked here; you keep your % over."],
    ["self", "Self-fulfilled", "You print it. Name, photo, price and ready-in are yours to edit."],
    ["off", "Off", "Hidden from shoppers. Nothing is deleted."],
];

const TONE = TONES.listings;
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

/** Which shelf a product sits on — same rule the backend uses. */
const roomOf = (p) => p.kind === "sign" ? "signs"
    : p.decoration === "embroidery" ? "embroidery"
        : p.decoration === "screen_print" ? "screenprint"
            : p.category === "colored" ? "colored" : "white";

const DEPT = { white: "White apparel", colored: "Colored apparel", embroidery: "Embroidery", screen_print: "Screen printing" };
const deptOf = (p) => (p.kind === "sign" ? `Signs · ${cap(p.category)}` : DEPT[p.decoration] || DEPT[p.category] || cap(p.category));
const deptKey = (p) => (p.kind === "sign" ? `sign-${p.category}` : p.decoration || p.category || "other");

/** Narrow the sheet to one kind of row: shown, hidden, locked to Demo, yours to edit. */
const SHOW = [
    { key: "all", label: "Everything", match: () => true },
    { key: "listed", label: "Listed", match: (p) => p.active },
    { key: "hidden", label: "Hidden", match: (p) => !p.active },
    { key: "locked", label: "Locked · consignment", match: (p) => !!p.consigned },
    { key: "mine", label: "Yours to edit", match: (p) => !p.consigned },
    { key: "edited", label: "Edited from master", match: (p) => !!p.edited },
];

/** One row of pills: a mono label, then the choices with counts. */
function PillRow({ label, choices, value, onPick, testid }) {
    return (
        <div className="flex flex-wrap items-center gap-1" data-testid={testid}>
            <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#4a4a4a]">{label}</span>
            {choices.map((c) => (
                <Pill key={c.key} active={value === c.key} count={c.count} testid={`${testid}-${c.key}`} onClick={() => onPick(c.key)}>{c.label}</Pill>
            ))}
        </div>
    );
}

/** The tabs are the shopper's header, item for item: Shirts · Signs. */
const SECTIONS = [
    {
        key: "all", label: "All listings",
        note: "Everything you sell in one list — and everything from the master catalog you don't carry yet.",
        match: () => true,
    },
    {
        key: "shirts", label: "Shirts",
        note: "Every garment is one product per color, plus embroidery and screen printing. Each row sets its own Ready-in: Same Day, 3–5 Days or 2–4 Weeks.",
        match: (p) => p.kind === "shirt",
    },
    {
        key: "signs", line: "signs", label: "Signs",
        note: "Your sign products. Set each one's Ready-in, or reprice every size on a product with one percentage.",
        match: (p) => p.kind === "sign",
    },
];

const LINES = [
    ["white", "White apparel"], ["colored", "Colored apparel"], ["dtf", "DTF gang sheets"],
    ["embroidery", "Embroidery"], ["screenprint", "Screen printing"], ["signs", "Signs"],
];
const LINE_COPY = Object.fromEntries(LINES.map(([k, v]) => [k, v.toLowerCase()]));
const TAB_OF = { white: "shirts", dtf: "shirts", colored: "shirts", embroidery: "shirts", screenprint: "shirts", signs: "signs" };

/**
 * The sheet: one column set for every row, so the same thing is always in the same place.
 * Each column says exactly what it holds — label on top, one plain sentence under it.
 */
const COLUMNS = [
    { key: "photo", label: "Photo", sub: "", w: "52px" },
    { key: "name", label: "Product name", sub: "word for word what shoppers read · click the photo to swap yours in", w: "minmax(300px,2fr)" },
    { key: "dept", label: "Department", sub: "the shelf it sits on", w: "124px" },
    { key: "ready", label: "Ready in", sub: "the turnaround shoppers are promised", w: "128px" },
    { key: "mode", label: "Who prints it", sub: "Demo on consignment · you · off", w: "184px", sellers: true },
    { key: "shown", label: "Shown to shoppers", sub: "listed = live in your shop", w: "104px" },
    { key: "base", label: "Price per piece", sub: "$ for the blank garment", w: "100px", tabs: ["all", "shirts"] },
    { key: "loc", label: "Each print location", sub: "$ added per printed side", w: "100px", tabs: ["all", "shirts"] },
    { key: "setup", label: "Setup fee", sub: "$ once per design", w: "88px", tabs: ["all", "shirts"] },
    { key: "min", label: "Minimum pieces", sub: "smallest order allowed", w: "88px", tabs: ["all", "shirts"] },
    { key: "scale", label: "Reprice every size", sub: "% of today · 110 = +10%", w: "108px", tabs: ["all", "signs"] },
    { key: "from", label: "Starts at", sub: "lowest price shoppers see", w: "88px" },
    { key: "actions", label: "Actions", sub: "save · hide · copy · preview · delete", w: "minmax(210px,auto)" },
];
const columnsFor = (tab, isHome) => COLUMNS.filter((c) => (!c.tabs || c.tabs.includes(tab)) && (!c.sellers || !isHome));
const gridOf = (cols) => ({ gridTemplateColumns: cols.map((c) => c.w).join(" ") });
/** The actions stay on screen while the middle of a wide sheet scrolls. */
const pinned = (key, bg) => (key === "actions" ? `sticky right-0 z-[1] ${bg} shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.25)]` : "");

const chip = "inline-flex items-center gap-1 whitespace-nowrap border px-1.5 py-px text-[11px] font-semibold uppercase";
const Cell = ({ children, className = "", title }) => (
    <div className={`flex min-w-0 items-center gap-1 px-1.5 ${className}`} title={title}>{children}</div>
);
const Dash = ({ why }) => <span className="w-full text-center text-[#9a9a9a]" title={why} aria-label={why}>—</span>;

const IconBtn = ({ onClick, disabled, testid, title, danger, children }) => (
    <button onClick={onClick} disabled={disabled} data-testid={testid} title={title} aria-label={title}
        className={`flex h-6 w-6 shrink-0 items-center justify-center border border-[#d0d0d0] bg-white disabled:opacity-40 ${danger
            ? "text-[#b42318] hover:border-[#b42318]" : "text-[#1a1a1a] hover:border-[#1a1a1a]"}`}>
        {children}
    </button>
);

function HeaderRow({ cols }) {
    return (
        <div className="sticky top-0 z-10 grid items-end border-b-2 border-[#1a1a1a] bg-[#fafafa]" style={gridOf(cols)} data-testid="catalog-sheet-header">
            {cols.map((c) => (
                <div key={c.key} className={`px-1.5 py-1.5 ${pinned(c.key, "bg-[#fafafa]")}`} title={`${c.label} — ${c.sub}`} data-testid={`catalog-col-${c.key}`}>
                    <p className="text-[11px] font-bold uppercase leading-tight text-[#1a1a1a]">{c.label}</p>
                    {c.sub && <p className="text-[10px] leading-tight text-[#4a4a4a]">{c.sub}</p>}
                </div>
            ))}
        </div>
    );
}

function Row({ p, cols, onSave, onDuplicate, onDelete, onRevert, busy, locked, isHome, consignOn }) {
    const [form, setForm] = useState(p);
    const [scale, setScale] = useState("");
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    useEffect(() => setForm(p), [p]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const dirty = form.name !== p.name || String(form.base_per_piece) !== String(p.base_per_piece)
        || String(form.location_fee) !== String(p.location_fee) || String(form.setup_fee) !== String(p.setup_fee)
        || String(form.min_order_qty) !== String(p.min_order_qty) || Number(scale) > 0;

    const pickPhoto = async (file) => {
        if (!file) return;
        setUploading(true);
        try { const rec = await uploadArtwork(file); await onSave(p, { photo: rec.url }); }
        catch { toast.error("Upload failed"); }
        finally { setUploading(false); }
    };

    const save = () => {
        const patch = { name: form.name, min_order_qty: Number(form.min_order_qty) || 0 };
        if (p.kind === "shirt") {
            patch.base_per_piece = Number(form.base_per_piece) || 0;
            patch.location_fee = Number(form.location_fee) || 0;
            if (p.setup_label) patch.setup_fee = Number(form.setup_fee) || 0;
        } else if (Number(scale) > 0) {
            patch.price_scale = Number(scale) / 100;
        }
        onSave(p, patch).then(() => setScale(""));
    };

    const num = (key, testid, step = "0.5") => (
        <input type="number" min="0" step={step} value={form[key] ?? 0} onChange={set(key)} disabled={locked}
            data-testid={`catalog-${testid}-${p.slug}`} aria-label={testid}
            className={`${cellCls} w-full ${locked ? "bg-[#f5f5f5] text-[#4a4a4a]" : ""}`} />
    );
    const shirt = p.kind === "shirt";
    const signsWhy = "Signs are priced by size — use Reprice every size";

    const cells = {
        photo: (
            <Cell className="py-1">
                <div className="relative h-9 w-10 shrink-0 overflow-hidden border border-[#d0d0d0] bg-[#fafafa]">
                    {p.image && <img src={mediaUrl(p.image)} alt="" className="h-full w-full object-contain" />}
                    {!locked && (
                        <>
                            <button onClick={() => fileRef.current?.click()} disabled={uploading} title="Use your own photo"
                                data-testid={`catalog-photo-${p.slug}`} aria-label="Use your own photo"
                                className="absolute inset-x-0 bottom-0 flex items-center justify-center bg-[#1a1a1a] py-px text-white bo-inv">
                                {uploading ? <Loader2 size={9} className="animate-spin" /> : <ImagePlus size={9} />}
                            </button>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden"
                                onChange={(e) => pickPhoto(e.target.files?.[0])} />
                        </>
                    )}
                </div>
            </Cell>
        ),
        name: (
            <Cell>
                <input className={`${inputCls} min-w-0 flex-1 px-1.5 py-0.5 font-semibold ${locked ? "border-transparent bg-transparent" : ""}`}
                    value={form.name || ""} onChange={set("name")} readOnly={locked} disabled={locked} title={form.name || ""}
                    data-testid={`catalog-name-${p.slug}`} />
                {p.inherited && (
                    <span title="Master SKU — from 13thav Demo's catalog. Hide it, don't delete it." data-testid={`catalog-master-${p.slug}`}
                        className={`${chip} border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]`}><Lock size={9} /> master</span>
                )}
                {p.edited && (
                    <span className={`${chip} border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]`} title="You changed this one from the master"
                        data-testid={`catalog-edited-${p.slug}`}>your edits</span>
                )}
                {p.photo_override && (
                    <span className={`${chip} border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]`} title="Shoppers see your photo, not the master's"
                        data-testid={`catalog-own-photo-${p.slug}`}>your photo</span>
                )}
            </Cell>
        ),
        dept: (
            <Cell title="The shelf this product sits on in your shop">
                <span className="truncate text-[12px] font-semibold text-[#1a1a1a]" data-testid={`catalog-dept-${p.slug}`}>{deptOf(p)}</span>
            </Cell>
        ),
        ready: (
            <Cell title="When shoppers get it — the Ready-in tag on the product card">
                <select value={p.ready_in || "weeks_2_4"} disabled={locked || busy} data-testid={`catalog-ready-${p.slug}`}
                    onChange={(e) => onSave(p, { ready_in: e.target.value })}
                    className={`${cellCls} w-full py-px text-[11px] font-semibold ${locked ? "bg-[#f5f5f5] text-[#4a4a4a]" : ""}`}>
                    {READY_IN.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
            </Cell>
        ),
        mode: (
            <Cell title={MODES.find(([k]) => k === p.fulfillment)?.[2] || "Who makes this product"}>
                <select value={p.fulfillment || (p.active ? "self" : "off")} disabled={busy} data-testid={`catalog-mode-${p.slug}`}
                    onChange={(e) => onSave(p, { fulfillment: e.target.value })}
                    className={`${cellCls} min-w-0 flex-1 py-px text-[11px] font-semibold ${p.fulfillment === "consignment" ? "border-[#b42318] text-[#b42318]" : ""}`}>
                    {MODES.filter(([k]) => k !== "consignment" || consignOn || p.fulfillment === "consignment").map(([k, label]) => (
                        <option key={k} value={k} disabled={k === "consignment" && (!consignOn || !p.source_slug)}>{label}</option>
                    ))}
                </select>
                {locked && (
                    <span className={`${chip} border-[#b42318] bg-[#fef3f2] text-[#b42318]`}
                        title="On consignment — 13thav Demo prints and ships it, so this row follows the Demo store"
                        data-testid={`catalog-consigned-${p.slug}`}><Lock size={9} /> locked</span>
                )}
            </Cell>
        ),
        shown: (
            <Cell title={p.active ? "Shoppers can buy this in your shop right now" : "Shoppers cannot see this one"}>
                {p.active
                    ? <span className={`${chip} border-[#067647] bg-[#ecfdf3] text-[#067647]`} data-testid={`catalog-listed-${p.slug}`}>listed</span>
                    : <span className={`${chip} border-[#b54708] bg-[#fffaeb] text-[#b54708]`} data-testid={`catalog-hidden-${p.slug}`}>hidden</span>}
            </Cell>
        ),
        base: <Cell>{shirt ? num("base_per_piece", "base") : <Dash why={signsWhy} />}</Cell>,
        loc: (
            <Cell>
                {shirt && !p.decoration ? num("location_fee", "location")
                    : shirt ? <Dash why={p.decoration === "embroidery" ? "Stitch tiers set the per-location price" : "Ink colors × quantity break set the per-piece price"} />
                        : <Dash why={signsWhy} />}
            </Cell>
        ),
        setup: <Cell>{shirt && p.setup_label ? num("setup_fee", "setup", "1") : <Dash why={shirt ? "No setup fee on this product" : signsWhy} />}</Cell>,
        min: <Cell>{shirt ? num("min_order_qty", "minqty", "1") : <Dash why="Signs have no minimum piece count" />}</Cell>,
        scale: (
            <Cell title="Type a percent of today's prices and Save — every size on this sign moves together">
                {shirt ? <Dash why="Shirts are priced per piece — use the $ columns" /> : (
                    <input type="number" min="1" step="1" placeholder="110" value={scale} disabled={locked}
                        onChange={(e) => setScale(e.target.value)} data-testid={`catalog-scale-${p.slug}`} aria-label="Reprice every size (% of today)"
                        className={`${cellCls} w-full ${locked ? "bg-[#f5f5f5] text-[#4a4a4a]" : ""}`} />
                )}
            </Cell>
        ),
        from: (
            <Cell title="The lowest price a shopper sees for this product">
                <span className="w-full text-right text-[12px] font-semibold tabular-nums text-[#1a1a1a]" data-testid={`catalog-from-${p.slug}`}>
                    {money(p.from_price)}
                </span>
            </Cell>
        ),
        actions: (
            <Cell className="justify-end py-1">
                {!locked && (
                    <>
                        <Btn onClick={save} disabled={!dirty || busy} testid={`catalog-save-${p.slug}`} tone="solid">
                            <Save size={10} /> Save
                        </Btn>
                        {isHome && (
                            <IconBtn onClick={() => onSave(p, { active: !p.active })} disabled={busy}
                                testid={`catalog-toggle-${p.slug}`} title={p.active ? "Hide it everywhere" : "List it"}>
                                {p.active ? <EyeOff size={12} /> : <Eye size={12} />}
                            </IconBtn>
                        )}
                        {p.can_revert && (
                            <IconBtn onClick={() => onRevert(p)} disabled={busy} testid={`catalog-revert-${p.slug}`}
                                title="Revert to the master">
                                <Undo2 size={12} />
                            </IconBtn>
                        )}
                        <IconBtn onClick={() => onDuplicate(p)} disabled={busy} testid={`catalog-duplicate-${p.slug}`}
                            title="Duplicate">
                            <Copy size={12} />
                        </IconBtn>
                    </>
                )}
                <Link to={`/account/drop-cards?kind=${p.kind}&slug=${p.slug}`} title="Make an Instagram drop card"
                    data-testid={`catalog-dropcard-${p.slug}`} aria-label="Make an Instagram drop card"
                    className="flex h-6 w-6 items-center justify-center border border-[#d0d0d0] bg-white text-[#1a1a1a] hover:border-[#1a1a1a]">
                    <Instagram size={12} />
                </Link>
                <a href={p.url} target="_blank" rel="noreferrer" data-testid={`catalog-view-${p.slug}`} title="Preview"
                    className="flex h-6 w-6 items-center justify-center border border-[#d0d0d0] bg-white text-[#1a1a1a] hover:border-[#1a1a1a]">
                    <ExternalLink size={12} />
                </a>
                {locked ? (
                    <span title="13thav Demo's stock — switch Who prints it to Self-fulfilled to edit it"
                        data-testid={`catalog-nolock-${p.slug}`}
                        className="whitespace-nowrap text-[11px] font-semibold uppercase text-[#b42318]">locked</span>
                ) : p.inherited ? (
                    <span title="A master SKU can only be hidden" data-testid={`catalog-nodelete-${p.slug}`}
                        className="whitespace-nowrap text-[11px] font-semibold uppercase text-[#4a4a4a]">no delete</span>
                ) : (
                    <IconBtn onClick={() => onDelete(p)} disabled={busy} testid={`catalog-delete-${p.slug}`}
                        title="Delete" danger>
                        <Trash2 size={12} />
                    </IconBtn>
                )}
            </Cell>
        ),
    };

    return (
        <div className={`grid min-h-[42px] items-center border-t border-[#d0d0d0] ${locked ? "border-l-4 border-l-[#b42318]" : "border-l-4 border-l-transparent"} ${p.active ? "" : "bg-[#fafafa]"}`}
            style={gridOf(cols)} data-testid={`catalog-product-${p.slug}`}>
            {cols.map((c) => <div key={c.key} className={`min-w-0 self-stretch flex items-center ${pinned(c.key, p.active ? "bg-white" : "bg-[#fafafa]")}`}>{cells[c.key]}</div>)}
        </div>
    );
}

export default function StoreProducts() {
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [library, setLibrary] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const tab = params.get("tab") || "all";
    const section = SECTIONS.find((s) => s.key === tab) || SECTIONS[0];

    const load = useCallback(() => {
        api.get("/store/catalog").then(({ data }) => setData(data))
            .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not load your catalog"));
    }, []);
    useEffect(load, [load]);

    useEffect(() => {
        if (tab !== "all" || library) return;
        api.get("/store/catalog/library").then(({ data }) => setLibrary(data)).catch(() => { });
    }, [tab, library]);

    const run = async (fn, msg) => {
        setBusy(true);
        try { await fn(); load(); setLibrary(null); if (msg) toast.success(msg); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const save = (p, patch) => run(() => api.patch(`/store/catalog/${p.kind}/${p.id}`, patch), "Product updated");
    const duplicate = (p) => run(() => api.post(`/store/catalog/${p.kind}/${p.id}/duplicate`), "Copy created — hidden until you list it");
    const remove = (p) => run(() => api.delete(`/store/catalog/${p.kind}/${p.id}`), "Product removed");
    const revert = (p) => run(async () => {
        const { data: res } = await api.post(`/store/catalog/${p.kind}/${p.id}/revert`);
        toast.success(res.note);
    });
    const addTemplate = (t) => run(() => api.post("/store/catalog/add", { kind: t.kind, slug: t.slug }), `${t.name} added to your store`);
    const starter = () => run(async () => {
        const { data: res } = await api.post("/store/catalog/starter");
        toast.success(res.total ? `${res.total} products copied into your store` : "You already have the whole catalog");
    });
    const push = () => run(async () => {
        const { data: res } = await api.post("/store/catalog/push");
        toast.success(res.note);
    });
    const setLine = (key, on) => run(async () => {
        await api.put("/stores/me/lines", { lines: { [key]: on } });
        toast.success(on ? `Your ${LINE_COPY[key]} counter is open.` : `Your ${LINE_COPY[key]} counter is closed.`);
    });

    if (err) return (
        <BackOffice><div className="mx-auto max-w-2xl py-16 text-center" data-testid="catalog-locked">
            <Store size={30} className="mx-auto text-zinc-500" />
            <h1 className="mt-3 font-display text-2xl font-black text-zinc-900">Products come with a store.</h1>
            <p className="mt-2 text-sm text-zinc-800">{err} Open a Storefront or a Pro Shop and the whole master catalog copies into your shop, yours to price.</p>
            <Link to="/spaces" className="mt-4 inline-block"><Btn tone="solid" testid="catalog-open-space">See the spaces</Btn></Link>
        </div></BackOffice>
    );
    if (!data) return <BackOffice><div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div></BackOffice>;

    const inTab = data.products.filter(section.match);
    const dept = params.get("dept") || "all";
    const show = SHOW.find((s) => s.key === params.get("show")) || SHOW[0];
    const deptChoices = [{ key: "all", label: "Every department", count: inTab.length },
        ...[...new Map(inTab.map((p) => [deptKey(p), deptOf(p)])).entries()].map(([key, label]) => ({ key, label, count: inTab.filter((p) => deptKey(p) === key).length }))];
    const shown = inTab.filter((p) => (dept === "all" || deptKey(p) === dept) && show.match(p));
    const setFilter = (key, value) => {
        const next = new URLSearchParams(params);
        if (value && value !== "all") next.set(key, value); else next.delete(key);
        setParams(next);
    };
    const cols = columnsFor(tab, data.is_home);
    const lineOn = section.line ? data.lines?.[section.line] !== false : true;
    const counts = (s) => (s.key === "all" ? data.products.length : data.products.filter(s.match).length);
    const consign = data.consignment || {};
    const rooms = consign.rooms || {};
    const roomInfo = section.line ? rooms[section.line] : null;
    const sectionLocked = !!roomInfo?.consigned;
    const lockedRooms = Object.values(rooms).filter((r) => r.consigned);
    const prices = shown.map((p) => Number(p.from_price) || 0).filter((v) => v > 0);
    const example = prices.length ? Math.min(...prices) : 15;
    const missing = (library?.templates || []).filter((t) => !t.added && !(consign.enabled && rooms[roomOf(t)]?.consigned));

    return (
        <BackOffice>
            <PageHead kicker="Products" tone={TONE} title={section.label} sub={section.note} testid="catalog-head"
                right={
                    <div className="text-right">
                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Listed / total</p>
                        <p className="font-mono text-2xl font-black tabular-nums text-zinc-900" data-testid="catalog-count">
                            {data.counts.listed}<span className="text-zinc-600">/{data.products.length}</span>
                        </p>
                        {!consign.enabled && (
                            <div className="mt-1.5">
                                {data.is_home ? (
                                    <Btn onClick={push} disabled={busy} testid="catalog-push">
                                        {busy ? <Loader2 className="animate-spin" size={11} /> : <Upload size={11} />} push to every shop
                                    </Btn>
                                ) : (
                                    <Btn onClick={starter} disabled={busy} testid="catalog-starter">
                                        {busy ? <Loader2 className="animate-spin" size={11} /> : <Wand2 size={11} />} load starter catalog
                                    </Btn>
                                )}
                            </div>
                        )}
                    </div>
                } />

            <div className="mt-3 flex flex-wrap gap-1" data-testid="catalog-tabs">
                {SECTIONS.map((s) => (
                    <Pill key={s.key} active={s.key === tab} count={counts(s)} testid={`catalog-tab-${s.key}`}
                        onClick={() => setParams(s.key === "all" ? {} : { tab: s.key })}>
                        {s.label}
                        {s.line && rooms[s.line]?.consigned && (
                            <Lock size={10} className={s.key === tab ? "" : "text-[#b42318]"}
                                data-testid={`catalog-tab-locked-${s.key}`} />
                        )}
                        {s.line && data.lines?.[s.line] === false && <span className="text-amber-700">off</span>}
                    </Pill>
                ))}
                <Link to="/account/classes" data-testid="catalog-tab-classes"
                    className="inline-flex items-center gap-1.5 border border-[#d0d0d0] bg-white px-2 py-1 text-[13px] font-semibold text-[#1a1a1a] hover:border-[#1a1a1a]">
                    Classes →
                </Link>
                <Link to="/account/listings" data-testid="catalog-tab-classifieds"
                    className="inline-flex items-center gap-1.5 border border-[#d0d0d0] bg-white px-2 py-1 text-[13px] font-semibold text-[#1a1a1a] hover:border-[#1a1a1a]">
                    Classifieds →
                </Link>
            </div>

            <div className="mt-2 space-y-1.5 border-y border-[#e5e5e5] py-2" data-testid="catalog-filters">
                <PillRow label="Department" testid="catalog-dept" value={dept} onPick={(v) => setFilter("dept", v)} choices={deptChoices} />
                <PillRow label="Show" testid="catalog-show" value={show.key} onPick={(v) => setFilter("show", v)}
                    choices={SHOW.filter((s) => data.is_home ? !["locked", "mine"].includes(s.key) : true).map((s) => ({ key: s.key, label: s.label, count: inTab.filter((p) => (dept === "all" || deptKey(p) === dept) && s.match(p)).length }))} />
            </div>

            {consign.enabled && roomInfo && (
                <div className="mt-3">
                    <ConsignmentSectionBar room={section.line} info={roomInfo} rate={consign.rate}
                        maxMarkup={consign.max_markup} example={example} onChanged={load} />
                </div>
            )}

            {consign.enabled && !section.line && (
                <div className="mt-3 border-2 border-[#b42318] bg-[#fef3f2] px-2.5 py-2" data-testid="catalog-consign-all">
                    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-[#1a1a1a]">
                        <span className="border border-[#b42318] bg-[#b42318] px-1.5 py-px text-[11px] font-bold uppercase text-white bo-inv">
                            <Lock size={10} className="mr-1 inline" /> {consign.locked} products locked
                        </span>
                        <span className="font-semibold">{consign.promise}</span>
                        Locked rows follow 13thav Demo. Change a row's <b>Who prints it</b> to Self-fulfilled to make it yours,
                        or Off to hide it — open a department below to set its percent over.
                    </p>
                    <p className="mt-1 flex flex-wrap gap-1.5">
                        {lockedRooms.map((r) => (
                            <button key={r.room} onClick={() => setParams({ tab: TAB_OF[r.room] || "all" })}
                                data-testid={`catalog-consign-jump-${r.room}`}
                                className="inline-flex items-center gap-1 border border-[#b42318] bg-white px-1.5 py-px text-[11px] font-semibold uppercase text-[#b42318] hover:bg-[#fef3f2]">
                                <Lock size={9} /> {r.label} · +{r.markup_pct}%
                            </button>
                        ))}
                    </p>
                </div>
            )}

            {section.line && !sectionLocked && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-black/10 bg-zinc-100 px-3 py-2"
                    data-testid="catalog-counter">
                    <div>
                        <p className="text-sm font-bold text-zinc-900">
                            {lineOn ? `Your ${LINE_COPY[section.line]} counter is open.` : `Your ${LINE_COPY[section.line]} counter is closed.`}
                        </p>
                        <p className="text-xs text-zinc-800">
                            {lineOn
                                ? "Shoppers are offered this in your shop and everything below is live."
                                : "Nobody is offered this in your shop. Your products stay here, priced and ready."}
                        </p>
                    </div>
                    <Toggle on={lineOn} disabled={busy} testid="catalog-counter-toggle"
                        onChange={(v) => setLine(section.line, v)} />
                </div>
            )}

            {tab === "all" && (
                <div className="mt-3 rounded-lg border border-black/10 bg-zinc-100 px-3 py-2" data-testid="catalog-departments">
                    <p className="text-sm font-bold text-zinc-900">Departments you run</p>
                    <p className="text-xs text-zinc-800">Close a department and shoppers stop seeing it in your shop. Your products stay here, priced and ready.</p>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
                        {LINES.map(([key, label]) => (
                            <label key={key} className="inline-flex items-center gap-2 text-[13px] text-zinc-900" data-testid={`catalog-line-${key}`}>
                                <Toggle on={data.lines?.[key] !== false} disabled={busy || !!rooms[key]?.consigned}
                                    testid={`catalog-line-toggle-${key}`} onChange={(v) => setLine(key, v)} />
                                {label}
                                {rooms[key]?.consigned && <Lock size={10} className="text-[#b42318]" />}
                            </label>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-3 space-y-3">
                {shown.length ? (
                    <div className={sectionLocked ? "border-2 border-[#b42318]" : ""} data-testid={sectionLocked ? "catalog-locked-frame" : undefined}>
                        <Section title={`${shown.length} ${section.label.toLowerCase()}`} tone={TONE} pad={false}
                            testid="catalog-products"
                            note={sectionLocked ? "Locked rows follow 13thav Demo — change Who prints it to edit one" : data.is_home ? "Edited here, live in every consigned shop" : "Edited here, live in your shop"}>
                            <div className="overflow-x-auto">
                                <div className="min-w-[1180px]" data-testid="catalog-sheet">
                                    <HeaderRow cols={cols} />
                                    {shown.map((p) => (
                                        <Row key={`${p.kind}-${p.id}`} p={p} cols={cols} onSave={save} onDuplicate={duplicate}
                                            onDelete={remove} onRevert={revert} busy={busy} isHome={data.is_home}
                                            consignOn={!!consign.enabled && !!consign.eligible} locked={!!p.consigned} />
                                    ))}
                                </div>
                            </div>
                        </Section>
                    </div>
                ) : (
                    <div className="rounded-lg border border-black/10 bg-white p-8 text-center" data-testid="catalog-empty">
                        <Package size={28} className="mx-auto text-zinc-400" />
                        <p className="mt-2.5 text-sm text-zinc-800">
                            {inTab.length ? "Nothing matches those filters." : `Nothing on your ${section.label.toLowerCase()} shelf yet.`}
                        </p>
                        <button onClick={() => setParams(inTab.length && tab !== "all" ? { tab } : {})} data-testid="catalog-see-all"
                            className="mt-3 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-900 hover:underline">
                            {inTab.length ? "clear filters →" : "see all listings →"}
                        </button>
                    </div>
                )}

                {tab === "all" && !data.is_home && !data.consignment?.enabled && (
                    !library ? (
                        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-zinc-500" /></div>
                    ) : missing.length ? (
                        <Section title="Not on your shelves yet" tone={TONE} pad={false} testid="catalog-library"
                            note={`${missing.length} master products you don't carry`}>
                            {missing.map((t) => (
                                <div key={`${t.kind}-${t.slug}`} className="flex items-center gap-3 border-t border-black/10 px-2.5 py-2 first:border-t-0"
                                    data-testid={`library-item-${t.slug}`}>
                                    <div className="h-11 w-11 shrink-0 overflow-hidden rounded border border-black/10 bg-zinc-50">
                                        {t.image && <img src={mediaUrl(t.image)} alt="" className="h-full w-full object-contain" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-display text-sm font-bold text-zinc-900">{t.name}</p>
                                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-700">
                                            {t.decoration ? t.decoration.replace("_", " ") : t.category} · from {money(t.from_price)}
                                        </p>
                                    </div>
                                    <Btn onClick={() => addTemplate(t)} disabled={busy} testid={`library-add-${t.slug}`} tone="solid">
                                        <Plus size={10} /> add to my store
                                    </Btn>
                                </div>
                            ))}
                        </Section>
                    ) : (
                        <p className="rounded-lg border border-black/10 bg-zinc-50 px-3 py-2 text-sm text-zinc-800"
                            data-testid="catalog-library-complete">
                            You carry every product in the master catalog.
                        </p>
                    )
                )}
            </div>
        </BackOffice>
    );
}
